import { formatNumber } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { format } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import NP from 'number-precision';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CalcExchangeRequestViewModel, CalcExchangeResponseViewModel, ExchangeCurrencyParams } from '../../../model/src/public-api';
import { isNullOrEmpty } from '../../../util/src/public-api';
import { ExchangeRate } from './../../../model/src/lib/exchange-rate.model';
import { doRoundByDecimalLength } from './../../../util/src/lib/do-round-by-decimal-length';
import { AppConfig } from './../../config/app.config';
import { ApiAccountingCommonService } from './api-accounting-common.service';
import { ApiCommonService } from './api-common.service';

@Injectable({
  providedIn: 'root',
})
export class ExchangeRateService {
  private exchangeRates: {
    timestamp: string;
    fromCurrency: string;
    toCurrency: string;
    rate: number;
  }[] = [];

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private appConfig: AppConfig,
    private apiAccountingCommonService: ApiAccountingCommonService,
    private apiCommonService: ApiCommonService,
    private logger: NGXLogger
  ) {}

  /**
   * @description 幣別轉換計算
   * @param fromCurrency 原始幣別
   * @param toCurrency 轉換幣別
   * @param amount 金額
   * @returns 幣別換算結果
   */
  exchange(
    fromCurrency: string,
    toCurrency: string,
    amount: number
  ): Observable<number | null> {
    const resultSubject = new BehaviorSubject<number | null>(null);
    this.getExchangeRate(fromCurrency, toCurrency).subscribe((result) => {
      resultSubject.next(NP.times(amount, result));
    });
    return resultSubject.asObservable();
  }

  /**
   * @description 幣別轉換計算，並且依據轉換幣別處理小數位數長度處理
   * @param fromCurrency 原始幣別
   * @param toCurrency 轉換幣別
   * @param amount 金額
   * @returns 幣別換算結果
   */
  exchangeWithCurrencyFormat(
    fromCurrency: string,
    toCurrency: string,
    amount: number
  ): Observable<number | null> {
    const resultSubject = new BehaviorSubject<number | null>(null);
    this.getExchangeRate(fromCurrency, toCurrency).subscribe((result) => {
      const decimalLength =
        this.appConfig.getDecimalLengthByCurrencyCode(toCurrency).minFractionDigits;
      resultSubject.next(
        doRoundByDecimalLength(NP.times(amount, result), decimalLength)
      );
    });
    return resultSubject.asObservable();
  }

  private getExchangeRate(
    fromCurrency: string,
    toCurrency: string
  ): Observable<number> {
    if (!fromCurrency) {
      this.logger.error('fromCurrency can not empty.');
      return of(1);
    }

    if (!toCurrency) {
      this.logger.error('toCurrency can not empty.');
      return of(1);
    }

    if (fromCurrency === toCurrency) {
      return of(1);
    }
    const timestamp = format(new Date(), 'yyyy-MM-dd');

    const exchangeRateData = this.exchangeRates.find(
      (item) =>
        item.fromCurrency === fromCurrency &&
        item.toCurrency === toCurrency &&
        item.timestamp === timestamp
    );

    if (exchangeRateData) {
      return of(exchangeRateData.rate);
    } else {
      return this.apiAccountingCommonService
        .doPost<{
          exchangeRate: ExchangeRate[];
        }>('/AccountingCommon/ExchangeRate', {
          filters: {
            baseCurrencyCd: fromCurrency,
            exchangeCurrencyCd: toCurrency,
            quotedDate: timestamp,
          },
        })
        .pipe(
          switchMap((result) => {
            if (
              result &&
              result.exchangeRate &&
              result.exchangeRate.length > 0
            ) {
              const targeExchangeRate = result.exchangeRate.find(
                (item) =>
                  item.baseCurrencyCd === fromCurrency &&
                  item.exchangeCurrencyCd === toCurrency
              );
              if (targeExchangeRate) {
                return of(targeExchangeRate.middleRate);
              } else {
                this.logger.warn(
                  `Can not find [${fromCurrency}] to [${toCurrency}] exchange rate`
                );
                return of(1);
              }
            } else {
              this.logger.warn(
                `Can not find [${fromCurrency}] to [${toCurrency}] exchange rate`
              );
              return of(1);
            }
          })
        );
    }
  }

  getExchangeResult(params: ExchangeCurrencyParams): Observable<CalcExchangeResponseViewModel> {
    if (
      params.srcCurrency === params.targetCurrency ||
      isNullOrEmpty(params.amount) || params.amount === 0
    ) {
      const defaultResult = this.getDefaultExchangeResult(params);
      return of(defaultResult);
    }

    const req: CalcExchangeRequestViewModel = {
      fromCurrency: params.srcCurrency,
      toCurrency: params.targetCurrency,
      exchangeRateSrc: isNullOrEmpty(params.specifyExchangeRate) ? 0 : 1,
      currencyDate: params.exchangeDate ? new Date(params.exchangeDate) : undefined,
      companyId: params.companyId,
      specifyExchangeRate: params.specifyExchangeRate,
      specifyCarryDigit: isNullOrEmpty(params.step) ? undefined : this.getCarrayDigit(params.step!),
      specifyCarryDigitMode: isNullOrEmpty(params.round) ? undefined : this.getCarrayDigitMode(params.round!),
      calcDirection: params.calcDirection ?? 0,
      amountList: [{ amount: params.amount }]
    };
    return this.apiCommonService.exchangeCalcPost(req);
  }

  getDefaultExchangeResult(params: ExchangeCurrencyParams): CalcExchangeResponseViewModel {
    const targetFormat = this.appConfig.getNumberFormatByCurrencyCode(params.targetCurrency!);
    const srcFormat = this.appConfig.getNumberFormatByCurrencyCode(params.srcCurrency!);
    const result: CalcExchangeResponseViewModel = {
      exchangeRate: 1,
      currency: params.targetCurrency ?? params.srcCurrency,
      amountList: [
        {
          amount: params.amount,
          formattedAmount: isNullOrEmpty(params.amount) ? undefined : formatNumber(params.amount!, this.locale, targetFormat ?? srcFormat)
        }
      ]
    };

    return result;
  }

  private getCarrayDigit(step: number): number {
    const str = step.toString();
    return str.indexOf('.') > -1 ? str.split('.')[1].length : 0 - (str.length - 1);
  }

  private getCarrayDigitMode(round: 'off' | 'up' | 'down'): number {
    return round === 'up' ? 2 : round === 'down' ? 1 : 0;
  }
}
